import { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext(null);
const LS_KEY = "solano_theme"; // "light" | "dark" | "system"

function getSystemPref() {
  try {
    return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark" : "light";
  } catch { return "light"; }
}

function readStored() {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {}
  return "system";
}

function applyTheme(mode) {
  const resolved = mode === "system" ? getSystemPref() : mode;
  const root = document.documentElement;
  if (resolved === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  root.setAttribute("data-theme", resolved);
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readStored);

  useEffect(() => {
    applyTheme(mode);
    try { localStorage.setItem(LS_KEY, mode); } catch {}
  }, [mode]);

  // React to OS theme changes when in "system" mode
  useEffect(() => {
    if (mode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme("system");
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [mode]);

  const setMode = useCallback((m) => {
    if (m === "light" || m === "dark" || m === "system") setModeState(m);
  }, []);

  const resolved = mode === "system" ? getSystemPref() : mode;

  return (
    <ThemeContext.Provider value={{ mode, setMode, resolved }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
